"use client";
import { useCallback, useEffect, useState } from "react";
import { PixelText } from "@/components/ui/pixel-text";

export type MoveInfo = {
  id: string;
  name: string;
  type: string;
  power: number;
  accuracy: number;
  pp: number;
  ppMax: number;
};

interface BattleMoveSelectProps {
  moves: MoveInfo[];
  enabled: boolean;
  onSelect: (moveId: string) => void;
  onBack: () => void;
}

const CREAM = "#f5ecd6";
const INK = "#1a1a1a";

const TYPE_COLORS: Record<string, string> = {
  sales: "#8B4513",
  tech: "#2F4F4F",
  finance: "#8B7355",
  creative: "#6B3D6B",
  ops: "#4A6741",
  hr: "#7B5E3D",
  normal: "#6B6B6B",
};

function typeColor(type: string): string {
  return TYPE_COLORS[type.toLowerCase()] ?? TYPE_COLORS.normal;
}

function typeAbbrev(type: string): string {
  return type.slice(0, 4).toUpperCase();
}

function ppColor(pp: number, ppMax: number): string {
  if (ppMax <= 0) return CREAM;
  const ratio = pp / ppMax;
  if (ratio <= 0.25) return "#a83a2a"; // brick red
  if (ratio <= 0.5) return "#b8923a";  // amber
  return CREAM;
}

// 2x2 grid, row-major:
//   index 0 (top-left)  index 1 (top-right)
//   index 2 (bot-left)  index 3 (bot-right)
const SLOTS = [0, 1, 2, 3] as const;

export function BattleMoveSelect({
  moves,
  enabled,
  onSelect,
  onBack,
}: BattleMoveSelectProps) {
  const [cursor, setCursor] = useState(0);
  const [focused, setFocused] = useState(true);

  const isSelectable = useCallback(
    (i: number) => {
      const m = moves[i];
      return !!m && m.pp > 0;
    },
    [moves]
  );

  const move = useCallback((dx: number, dy: number) => {
    setCursor((c) => {
      const col = c % 2;
      const row = (c - col) / 2;
      const nextCol = Math.max(0, Math.min(1, col + dx));
      const nextRow = Math.max(0, Math.min(1, row + dy));
      return nextRow * 2 + nextCol;
    });
  }, []);

  useEffect(() => {
    if (!enabled || !focused) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key;
      if (
        key === "ArrowUp" ||
        key === "ArrowDown" ||
        key === "ArrowLeft" ||
        key === "ArrowRight" ||
        key === "Enter" ||
        key === "z" ||
        key === "Z" ||
        key === "x" ||
        key === "X" ||
        key === "Escape"
      ) {
        e.preventDefault();
      }

      switch (key) {
        case "ArrowUp":
          move(0, -1);
          break;
        case "ArrowDown":
          move(0, 1);
          break;
        case "ArrowLeft":
          move(-1, 0);
          break;
        case "ArrowRight":
          move(1, 0);
          break;
        case "Enter":
        case "z":
        case "Z": {
          const m = moves[cursor];
          if (m && m.pp > 0) {
            onSelect(m.id);
          }
          break;
        }
        case "x":
        case "X":
        case "Escape":
          onBack();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled, focused, cursor, move, moves, onSelect, onBack]);

  const dimmed = !enabled;

  return (
    <div
      className={`flex flex-col gap-3 ${
        dimmed ? "opacity-50 pointer-events-none" : ""
      }`}
      style={{
        backgroundColor: CREAM,
        border: `2px solid ${INK}`,
        padding: "1rem 1.25rem",
        imageRendering: "pixelated",
      }}
      onMouseEnter={() => setFocused(true)}
    >
      <PixelText
        as="div"
        glow="none"
        size="md"
        className="!text-[#1a1a1a] !tracking-normal !normal-case"
      >
        Choose a move
      </PixelText>

      <div
        className="grid grid-cols-2 gap-2"
        role="menu"
        aria-label="Move selection"
      >
        {SLOTS.map((i) => {
          const m = moves[i];
          const active = i === cursor && focused && !!m;
          const selectable = isSelectable(i);

          // Empty placeholder tile (still styled, unfocusable)
          if (!m) {
            return (
              <div
                key={`empty-${i}`}
                aria-hidden
                style={{
                  backgroundColor: CREAM,
                  border: `2px solid ${INK}`,
                  padding: "0.625rem 0.75rem",
                  minHeight: "4.25rem",
                  opacity: 0.35,
                  imageRendering: "pixelated",
                }}
              />
            );
          }

          const tileBg = active ? INK : CREAM;
          const tileFg = active ? CREAM : INK;
          const tileOpacity = selectable ? 1 : 0.45;
          const powerLabel = m.power > 0 ? `Power: ${m.power}` : "Power: \u2014";

          return (
            <button
              key={m.id}
              type="button"
              role="menuitem"
              disabled={!selectable}
              aria-disabled={!selectable}
              onMouseEnter={() => {
                setCursor(i);
                setFocused(true);
              }}
              onClick={() => {
                if (selectable) onSelect(m.id);
              }}
              className="relative font-mono font-bold uppercase tracking-widest text-left"
              style={{
                backgroundColor: tileBg,
                color: tileFg,
                border: `2px solid ${INK}`,
                padding: "0.625rem 0.75rem 0.625rem 1.75rem",
                minHeight: "4.25rem",
                opacity: tileOpacity,
                cursor: selectable ? "pointer" : "not-allowed",
                transition: "none",
                imageRendering: "pixelated",
                display: "flex",
                flexDirection: "column",
                gap: "0.375rem",
              }}
            >
              {active && (
                <span
                  aria-hidden
                  style={{
                    position: "absolute",
                    left: "0.5rem",
                    top: "0.75rem",
                    color: CREAM,
                    fontSize: "0.95rem",
                    lineHeight: 1,
                  }}
                >
                  &#9654;
                </span>
              )}

              {/* Top row: move name + type badge */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "0.5rem",
                }}
              >
                <span
                  style={{
                    fontSize: "0.95rem",
                    lineHeight: 1,
                  }}
                >
                  {m.name}
                </span>
                <span
                  aria-label={`Type ${m.type}`}
                  style={{
                    backgroundColor: typeColor(m.type),
                    color: "#ffffff",
                    border: `2px solid ${INK}`,
                    padding: "0.125rem 0.375rem",
                    fontSize: "0.65rem",
                    lineHeight: 1,
                    letterSpacing: "0.1em",
                    imageRendering: "pixelated",
                  }}
                >
                  {typeAbbrev(m.type)}
                </span>
              </div>

              {/* Bottom row: PP (left) + Power (right) */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "0.5rem",
                  fontSize: "0.65rem",
                  letterSpacing: "0.1em",
                  marginTop: "auto",
                }}
              >
                <span
                  style={{
                    // Only override color when PP is low/critical; otherwise inherit tileFg.
                    color: ppColor(m.pp, m.ppMax) !== CREAM ? ppColor(m.pp, m.ppMax) : tileFg,
                  }}
                >
                  {`PP ${m.pp}/${m.ppMax}`}
                </span>
                <span>{powerLabel}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
